import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Loader2, LogOut, Lock, Mail, Trash2, ChevronDown, Users, UserCog, Handshake, Flag, HeartHandshake, ArrowLeft, Save,
} from "lucide-react";

const ROLE_ICONS = {
  Player: Users, Manager: UserCog, Sponsor: Handshake, Umpire: Flag, Volunteer: HeartHandshake,
};
const STATUS_OPTS = ["new", "contacted", "approved"];
const STATUS_STYLE = {
  new: "bg-[#FF3B30]/15 text-[#FF6B63] border-[#FF3B30]/40",
  contacted: "bg-[#F4A261]/15 text-[#F4A261] border-[#F4A261]/40",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
};

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("wabl_token", data.token);
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-white/20 pl-11 pr-4 py-3 text-white placeholder:text-[#5b626b] focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#141414] border border-white/10 p-8 sm:p-10"
      >
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0] hover:text-white mb-8">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <img
            src="https://customer-assets.emergentagent.com/job_adult-baseball-hub/artifacts/b8ws7h83_wabl%20logo.jpg"
            alt="WABL"
            className="h-12 w-12 object-contain"
          />
          <h1 className="text-3xl uppercase tracking-tight">Admin</h1>
        </div>
        <p className="text-[#8A94A0] font-medium mb-8">Sign in to view league sign-ups.</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5b626b]" size={18} />
            <input
              type="email"
              data-testid="admin-email"
              className={inputCls}
              placeholder="admin@wabl.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5b626b]" size={18} />
            <input
              type="password"
              data-testid="admin-password"
              className={inputCls}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-[#FF6B63] text-sm font-bold">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full font-heading uppercase tracking-wide bg-[#FF3B30] text-white hover:bg-[#FF6B63] disabled:opacity-60 transition-colors px-8 py-4 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="animate-spin" size={20} /> Signing in...</> : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function RostersManager({ onAuthError }) {
  const [teams, setTeams] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);

  useEffect(() => {
    api.get("/admin/teams")
      .then((res) => {
        setTeams(res.data);
        const d = {}, r = {};
        res.data.forEach((t) => {
          d[t.slug] = (t.roster || []).join("\n");
          r[t.slug] = { wins: t.wins ?? 0, losses: t.losses ?? 0, ties: t.ties ?? 0 };
        });
        setDrafts(d);
        setRecords(r);
      })
      .catch((err) => { if (err.response?.status === 401) onAuthError(); })
      .finally(() => setLoading(false));
  }, [onAuthError]);

  const save = async (slug) => {
    setSavingSlug(slug);
    try {
      const roster = (drafts[slug] || "").split("\n").map((x) => x.trim()).filter(Boolean);
      const wins = Number(records[slug]?.wins) || 0;
      const losses = Number(records[slug]?.losses) || 0;
      const ties = Number(records[slug]?.ties) || 0;
      await api.put(`/admin/teams/${slug}/roster`, { roster });
      await api.put(`/admin/teams/${slug}/record`, { wins, losses, ties });
      setTeams((ts) => ts.map((t) => (t.slug === slug ? { ...t, roster, wins, losses, ties } : t)));
      toast.success("Team saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingSlug(null); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-[#8A94A0]"><Loader2 className="animate-spin mr-2" /> Loading teams...</div>;
  }

  return (
    <div className="space-y-5" data-testid="rosters-manager">
      <p className="text-[#8A94A0] font-medium text-sm">
        Enter one player per line and set each team's record. Click Save to publish to the team's page.
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        {teams.map((t) => (
          <div key={t.slug} data-testid={`roster-editor-${t.slug}`} className="border border-white/10 bg-[#141414] p-5">
            <div className="flex items-center gap-3 mb-4">
              <img src={t.logo} alt={t.name} className="h-11 w-11 object-cover border border-white/10" />
              <div>
                <h3 className="text-lg uppercase tracking-tight leading-none">{t.name}</h3>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0] mt-1">
                  {(drafts[t.slug] || "").split("\n").map((x) => x.trim()).filter(Boolean).length} players
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0] mb-1 block">Wins</label>
                <input
                  type="number" min="0"
                  data-testid={`wins-${t.slug}`}
                  value={records[t.slug]?.wins ?? 0}
                  onChange={(e) => setRecords((r) => ({ ...r, [t.slug]: { ...r[t.slug], wins: e.target.value } }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none font-medium"
                />
              </div>
              <span className="font-heading text-2xl text-white/30 mt-5">-</span>
              <div className="flex-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0] mb-1 block">Losses</label>
                <input
                  type="number" min="0"
                  data-testid={`losses-${t.slug}`}
                  value={records[t.slug]?.losses ?? 0}
                  onChange={(e) => setRecords((r) => ({ ...r, [t.slug]: { ...r[t.slug], losses: e.target.value } }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none font-medium"
                />
              </div>
              <span className="font-heading text-2xl text-white/30 mt-5">-</span>
              <div className="flex-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0] mb-1 block">Ties</label>
                <input
                  type="number" min="0"
                  data-testid={`ties-${t.slug}`}
                  value={records[t.slug]?.ties ?? 0}
                  onChange={(e) => setRecords((r) => ({ ...r, [t.slug]: { ...r[t.slug], ties: e.target.value } }))}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none font-medium"
                />
              </div>
            </div>

            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0] mb-1 block">Roster</label>
            <textarea
              data-testid={`roster-textarea-${t.slug}`}
              rows={6}
              value={drafts[t.slug] || ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [t.slug]: e.target.value }))}
              placeholder={"John Smith\nMike Jones\n..."}
              className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-white placeholder:text-[#5b626b] focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none font-medium text-sm"
            />
            <button
              data-testid={`save-roster-${t.slug}`}
              onClick={() => save(t.slug)}
              disabled={savingSlug === t.slug}
              className="mt-3 w-full font-heading uppercase tracking-wide bg-[#FF3B30] text-white hover:bg-[#FF6B63] disabled:opacity-60 transition-colors px-4 py-3 flex items-center justify-center gap-2 text-sm"
            >
              {savingSlug === t.slug ? <><Loader2 className="animate-spin" size={16} /> Saving...</> : <><Save size={16} /> Save</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [signups, setSignups] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("signups");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, stRes] = await Promise.all([
        api.get("/admin/signups", { params: { role: filter } }),
        api.get("/admin/stats"),
      ]);
      setSignups(sRes.data);
      setStats(stRes.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [filter, onLogout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/signups/${id}`, { status });
      setSignups((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/signups/${id}`);
      setSignups((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filters = ["all", "Player", "Manager", "Sponsor", "Umpire", "Volunteer"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur-xl z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_adult-baseball-hub/artifacts/b8ws7h83_wabl%20logo.jpg"
              alt="WABL" className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="text-xl uppercase tracking-tight leading-none">WABL Admin</h1>
              <p className="text-xs text-[#8A94A0] font-bold uppercase tracking-[0.2em]">Sign-up Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="view-site-link" className="hidden sm:block text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0] hover:text-white">View Site</Link>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="flex items-center gap-2 border border-white/20 hover:border-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border border-white/10 divide-x divide-white/10 mb-10">
          <div className="p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">Total Sign-ups</p>
            <p data-testid="stat-total" className="font-heading text-5xl mt-2">{stats?.total ?? "—"}</p>
          </div>
          <div className="p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">New</p>
            <p data-testid="stat-new" className="font-heading text-5xl mt-2 text-[#FF3B30]">{stats?.new ?? "—"}</p>
          </div>
          <div className="p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">Players</p>
            <p className="font-heading text-5xl mt-2">{stats?.by_role?.Player ?? "—"}</p>
          </div>
          <div className="p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">Sponsors</p>
            <p className="font-heading text-5xl mt-2">{stats?.by_role?.Sponsor ?? "—"}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {[["signups", "Sign-ups"], ["rosters", "Team Rosters"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
                tab === key ? "border-[#FF3B30] text-white" : "border-transparent text-[#8A94A0] hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "rosters" ? (
          <RostersManager onAuthError={onLogout} />
        ) : (
        <>
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-all ${
                filter === f ? "bg-white text-[#0A0A0A] border-white" : "border-white/20 text-[#8A94A0] hover:border-white/50"
              }`}
            >
              {f === "all" ? "All" : f}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#8A94A0]">
            <Loader2 className="animate-spin mr-2" /> Loading...
          </div>
        ) : signups.length === 0 ? (
          <div data-testid="empty-state" className="border border-white/10 py-24 text-center text-[#8A94A0] font-medium">
            No sign-ups yet for this filter.
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="signups-list">
            {signups.map((s) => {
              const Icon = ROLE_ICONS[s.role] || Users;
              return (
                <div key={s.id} data-testid={`signup-row-${s.id}`} className="p-5 sm:p-6 grid lg:grid-cols-12 gap-4 items-start hover:bg-white/[0.02] transition-colors">
                  <div className="lg:col-span-4">
                    <div className="flex items-center gap-3">
                      <span className="bg-[#FF3B30]/15 border border-[#FF3B30]/30 p-2"><Icon size={18} className="text-[#FF3B30]" /></span>
                      <div>
                        <p className="font-bold text-lg leading-tight">{s.name}</p>
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8A94A0]">{s.role}{s.position ? ` · ${s.position}` : ""}</p>
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-4 text-sm font-medium text-[#C7CDD4] space-y-1">
                    <p className="truncate">✉ {s.email}</p>
                    {s.phone && <p>☎ {s.phone}</p>}
                    {s.experience && <p className="text-[#8A94A0]">Exp: {s.experience}</p>}
                    {s.message && <p className="text-[#8A94A0] italic line-clamp-2">"{s.message}"</p>}
                  </div>
                  <div className="lg:col-span-2">
                    <div className="relative inline-block w-full">
                      <select
                        data-testid={`status-select-${s.id}`}
                        value={s.status}
                        onChange={(e) => updateStatus(s.id, e.target.value)}
                        className={`appearance-none w-full border px-3 py-2 pr-8 text-xs font-bold uppercase tracking-wide cursor-pointer ${STATUS_STYLE[s.status]}`}
                      >
                        {STATUS_OPTS.map((o) => <option key={o} value={o} className="bg-[#141414] text-white">{o}</option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-70" />
                    </div>
                  </div>
                  <div className="lg:col-span-2 lg:text-right">
                    <button
                      data-testid={`delete-signup-${s.id}`}
                      onClick={() => remove(s.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#8A94A0] hover:text-[#FF3B30] transition-colors"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("wabl_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem("wabl_token"))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem("wabl_token");
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#8A94A0]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!user) return <LoginView onSuccess={setUser} />;
  return <Dashboard onLogout={logout} />;
}
